<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageList from './MessageList.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';

const { assistantId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['log-entry']);

const { t } = useI18n();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const thinkingLabel = ref('');

// Ordered list of thinking messages that cycle during loading.
// They give the user a sense of what the AI might be doing even before
// we receive real-time tool events (sync API).
const thinkingMessages = [
  t('CAPTAIN.PLAYGROUND.THINKING_TOOLS.search_documentation'),
  t('CAPTAIN.PLAYGROUND.THINKING_TOOLS.search_past_conversations'),
  t('CAPTAIN.PLAYGROUND.THINKING_TOOLS.cetakers_flow_get_products'),
];

let thinkingTimer = null;
let thinkingIndex = 0;

const startThinkingCycle = () => {
  thinkingIndex = 0;
  thinkingLabel.value = thinkingMessages[0];
  thinkingTimer = setInterval(() => {
    thinkingIndex = (thinkingIndex + 1) % thinkingMessages.length;
    thinkingLabel.value = thinkingMessages[thinkingIndex];
  }, 1800);
};

const stopThinkingCycle = () => {
  if (thinkingTimer) {
    clearInterval(thinkingTimer);
    thinkingTimer = null;
  }
  thinkingLabel.value = '';
};

onUnmounted(stopThinkingCycle);

const formatMessagesForApi = () => {
  return messages.value.map(message => {
    const payload = {
      role: message.sender,
      content: message.content,
    };

    if (message.sender === 'assistant' && message.agentName) {
      payload.agent_name = message.agentName;
    }

    return payload;
  });
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
};

// Watch for assistant ID changes and reset conversation
watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';

  const requestTimestamp = new Date().toISOString();

  try {
    isLoading.value = true;
    startThinkingCycle();

    const { data } = await CaptainAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
    });

    const toolCalls = data.tool_calls_made || data.toolCallsMade || [];
    const duration = data.duration_ms || data.durationMs || null;

    messages.value.push({
      content: data.response,
      sender: 'assistant',
      agentName: data.agent_name,
      // List of tool names the backend called — used to render the
      // "Sumber yang diperiksa" badges below the message bubble.
      toolCallsMade: toolCalls,
      timestamp: new Date().toISOString(),
    });

    // Emit log entry for the debug panel
    emit('log-entry', {
      query: currentMessage,
      response: data.response,
      reasoning: data.reasoning || null,
      toolCallsMade: toolCalls,
      durationMs: duration,
      status: 'success',
      timestamp: requestTimestamp,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);

    const errorMessage = error?.response?.data?.response || 'Maaf Kak, terjadi kendala saat memproses balasan (Server Timeout / Error 500). Silakan coba lagi.';
    const errorReasoning = error?.response?.data?.reasoning || `HTTP Error: ${error?.message || '500 Server Error'}`;

    messages.value.push({
      content: errorMessage,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
    });

    // Emit error log entry for the debug panel
    emit('log-entry', {
      query: currentMessage,
      response: errorMessage,
      reasoning: errorReasoning,
      error: error?.message || 'Request failed',
      toolCallsMade: [],
      durationMs: null,
      status: 'error',
      timestamp: requestTimestamp,
    });
  } finally {
    stopThinkingCycle();
    isLoading.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  sendMessage();
};
</script>

<template>
  <div
    class="flex flex-col h-full rounded-xl border py-6 border-n-weak text-n-slate-11"
  >
    <div class="mb-8 px-6">
      <div class="flex justify-between items-center mb-1">
        <h3 class="text-lg font-medium">
          {{ t('CAPTAIN.PLAYGROUND.HEADER') }}
        </h3>
        <NextButton
          ghost
          sm
          slate
          icon="i-lucide-rotate-ccw"
          @click="resetConversation"
        />
      </div>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PLAYGROUND.DESCRIPTION') }}
      </p>
    </div>

    <MessageList
      :messages="messages"
      :is-loading="isLoading"
      :thinking-label="thinkingLabel"
    />

    <div
      class="flex items-center mx-6 bg-n-background outline outline-1 outline-n-weak rounded-xl p-3"
    >
      <input
        v-model="newMessage"
        class="flex-1 bg-transparent border-none focus:outline-none text-sm mb-0 text-n-slate-12 placeholder:text-n-slate-10"
        :placeholder="t('CAPTAIN.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @keydown.enter.exact="handleEnterKey"
      />
      <NextButton
        ghost
        sm
        :disabled="!newMessage.trim()"
        icon="i-lucide-send"
        @click="sendMessage"
      />
    </div>

    <p class="text-xs text-n-slate-11 pt-2 text-center">
      {{ t('CAPTAIN.PLAYGROUND.CREDIT_NOTE') }}
    </p>
  </div>
</template>
